import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AGENT_SELECTED_BOT_STORAGE_KEY } from '@/constants/agentWorkspace';
import type { Bot } from '@/services/api/bot.service';

export type AgentWorkspaceBotPick = {
  /** `bots.id` (UUID) */
  id: string;
  /** `bots.bot_id` slug — matches `conversations.bot_id` for API filters */
  bot_id: string;
  name: string;
};

type State = {
  /** Persisted (see partialize). */
  selection: AgentWorkspaceBotPick | null;
  /** Bumped when selection is cleared so bootstrap can re-run (e.g. admin revoked bot access). */
  workspaceEpoch: number;
  /** Session-only: modal visibility. */
  pickerOpen: boolean;
  /** Session-only: cached list for default-bot-on-navigate. */
  availableBots: Bot[];
  /** After live chat gate: navigate here once a bot is chosen. */
  pendingReturnPath: string | null;

  setSelection: (b: AgentWorkspaceBotPick) => void;
  clearSelection: () => void;
  openPicker: () => void;
  closePicker: () => void;
  setAvailableBots: (bots: Bot[]) => void;
  setPendingReturnPath: (path: string | null) => void;
  clearPendingReturnPath: () => void;
  /** Clears session UI + selection (e.g. logout). */
  resetAgentWorkspace: () => void;
};

export const useAgentSelectedBotStore = create<State>()(
  persist(
    (set) => ({
      selection: null,
      workspaceEpoch: 0,
      pickerOpen: false,
      availableBots: [],
      pendingReturnPath: null,

      setSelection: (b) =>
        set({
          selection: b,
          pickerOpen: false,
        }),

      clearSelection: () =>
        set((s) => ({
          selection: null,
          pickerOpen: false,
          pendingReturnPath: null,
          workspaceEpoch: s.workspaceEpoch + 1,
        })),

      openPicker: () => set({ pickerOpen: true }),

      closePicker: () => set({ pickerOpen: false }),

      setAvailableBots: (bots) => set({ availableBots: bots }),

      setPendingReturnPath: (path) => set({ pendingReturnPath: path }),

      clearPendingReturnPath: () => set({ pendingReturnPath: null }),

      resetAgentWorkspace: () =>
        set({
          selection: null,
          workspaceEpoch: 0,
          pickerOpen: false,
          availableBots: [],
          pendingReturnPath: null,
        }),
    }),
    {
      name: AGENT_SELECTED_BOT_STORAGE_KEY,
      partialize: (s) => ({ selection: s.selection }),
    }
  )
);

/** Sync read for post-login routing before zustand rehydrates. */
export function readPersistedAgentBotSelection(): AgentWorkspaceBotPick | null {
  try {
    const raw = localStorage.getItem(AGENT_SELECTED_BOT_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { state?: { selection?: AgentWorkspaceBotPick | null } };
    const s = p?.state?.selection;
    if (s?.id && s?.bot_id && s?.name) return s;
  } catch {
    /* ignore */
  }
  return null;
}

export function isAgentBotSelectionValid(
  user: { role?: string | null; agentBotIds?: string[] | null } | null | undefined,
  sel: AgentWorkspaceBotPick | null
): boolean {
  if (!user || user.role?.toLowerCase() !== 'agent' || !sel?.id) return false;
  const ids = user.agentBotIds;
  /** Until the session includes `agentBotIds`, do not treat a persisted pick as verified. */
  if (!Array.isArray(ids)) return false;
  if (ids.length === 0) return true;
  return ids.includes(sel.id);
}
